import { useEffect, useRef, useState, useCallback } from "react";
import { Page } from "@jobber/components/Page";
import { Card, Content, DatePicker, Grid, Heading, Select, Option, RadioGroup, RadioOption, Button, Icon, Tabs, Tab, Chips, Chip, Combobox, Text } from "@jobber/components";
import ListItem from "@/components/ListItem";
import { formatDate, formatDateDayjs, haversineDistance } from "@/utils";
import MapComponent from '@/components/Map/MapComponent';
import { useMapContext } from '@/contexts/MapContext';
import { createLatLng, formatAddress } from '@/components/Map/utils';
import TabWhap from "@/components/TabWhap";
import { trpc } from "@/services/trpc";
import { format, min, set, parseISO } from "date-fns";
import { formatInTimeZone } from 'date-fns-tz';
import { isPointInsideComplexGeoJSON } from "@/utils/geoJson";
import { JobsUseQueryType, TechniciansListUseQueryType, ServiceArea, ServiceAreaUseQueryType, SummaryEntry, MatchUseMutationType } from "@/services/trpc/types";

interface SuggestedOption {
  title: string;
  subtitle: string;
  caption: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

// type ServiceAreaType = RouterOutputs['serviceAreas']['create']
// type ServiceType = RouterOutputs['settings']['getDefaultService']
// type TimeSlots = RouterOutputs['services']['match']
type Preferences = 'noPreferences' | 'asap' | 'dateRange'

export default function MatcherPage() {
  const [isSearchView, setIsSearchView] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const { addMapMarker, setupMapAutocomplete, calculateMapRoute, isMapInitialized, mapInstance } = useMapContext();
  const serviceAreasQuery = trpc.serviceAreas.list.useQuery() as ServiceAreaUseQueryType
  // const defaultServiceQuery = trpc.settings.getDefaultService.useQuery()

  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  // const [selectedService, setSelectedService] = useState<ServiceType | undefined>(undefined)
  // useEffect(() => {
  //   if (defaultServiceQuery.data) {
  //     setSelectedService(defaultServiceQuery.data)
  //   }
  // }, [defaultServiceQuery.data])

  const [selectedArea, setSelectedArea] = useState<string | undefined>(undefined)
  const [preferences, setPreferences] = useState<Preferences>('noPreferences');
  const [dateRangeStart, setDateRangeStart] = useState<Date | undefined>(undefined);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | undefined>(undefined);
  const [suggestedOptions, setSuggestedOptions] = useState<SummaryEntry[]>([]);

  const handleServiceAreaChange = (value: string) => {
    setSelectedArea(value);
  }

  const getTitle = (option: SummaryEntry) => {
    return `${formatDateDayjs(option.start, "ddd MMMM DD")} | ${formatDateDayjs(option.start, "h:mm a").toUpperCase()} - ${formatDateDayjs(option.end, "h:mm a").toUpperCase()}`
  }


  const { data: jobs, isLoading: isJobsLoading } = trpc.jobs.list.useQuery() as JobsUseQueryType
  const { data: technicians, isLoading: isTechniciansLoading } = trpc.technicians.list.useQuery() as TechniciansListUseQueryType
  const { data: services, isLoading: isServicesLoading } = trpc.services.list.useQuery();
  const { data: timeWindowsList } = trpc.settings.getTimeWindows.useQuery()
  const matchMutation = trpc.services.match.useMutation() as MatchUseMutationType

  const isLoading = isJobsLoading || isTechniciansLoading || isServicesLoading;

  const [selectedTimeWindows, setSelectedTimeWindows] = useState<any[]>([])

  const getClosestServiceArea = useCallback((latitude: number, longitude: number) : ServiceArea | undefined => {
    let closestArea: ServiceArea | undefined = undefined
    let minDistance = Number.MAX_VALUE;

    serviceAreasQuery.data?.forEach((area : ServiceArea) => {
      if (minDistance === 0) return
      if (area.geoJson && area.geoJson.trim()) {
        console.log(area.geoJson);

        const isPointInsideGeoJSON = isPointInsideComplexGeoJSON([longitude, latitude], area.geoJson)
        console.log(isPointInsideGeoJSON);
        if (isPointInsideGeoJSON) {
          minDistance = 0
          closestArea = area
          return
        }
      }
      const distance = haversineDistance({ lat: latitude, lng: longitude }, { lat: area.latitude, lng: area.longitude });
      if (distance < minDistance) {
        minDistance = distance;
        closestArea = area;
      }
    });

    return closestArea;
  }, [serviceAreasQuery.data])

  // when services loaded set default service if empty
  // useEffect(() => {
  //   if (services?.length && !selectedService) {
  //     setSelectedService(services[0].externalId);
  //   }
  // }, [services, selectedService]);

  useEffect(() => {
    if (searchRef.current && isMapInitialized) {
      setupMapAutocomplete(searchRef.current, (place: google.maps.places.PlaceResult) => {
        if (place.geometry && place.geometry.location) {
          // addOrUpdateMapMarker(place.geometry.location);
          const area = getClosestServiceArea(place.geometry.location.lat(), place.geometry.location.lng())
          setSelectedArea(area?.id)
          if (area) {
            const areaFormatted = createLatLng(area.latitude, area.longitude)
            calculateMapRoute( areaFormatted, place.geometry.location)
          }
          const formattedAddress = formatAddress(place);
          if (searchRef.current) {
            searchRef.current.value = formattedAddress;
          }
        }
      });
    }
  }, [setupMapAutocomplete, isMapInitialized, getClosestServiceArea, calculateMapRoute]);

  useEffect(() => {
    if (selectedJob && jobs && mapInstance) {
      const job = jobs.find((job: { id: string; }) => job.id === selectedJob);
      if (job && job.latitude && job.longitude) {
        const position = createLatLng(job.latitude, job.longitude);
        addMapMarker(position);
        mapInstance.setCenter(position);
        mapInstance.setZoom(17);
      }
    }
  }, [selectedJob, jobs, addMapMarker, mapInstance]);

  const findBestOptionsClick = async () => {
    if (selectedArea) {
      const dateRange = preferences === 'dateRange' && (dateRangeStart && dateRangeEnd) ? {
        start: dateRangeStart.toISOString(),
        end: dateRangeEnd.toISOString()
      } : undefined
      await matchMutation.mutate({
        serviceAreaId: selectedArea,
        preferences: preferences,
        dateRange,
        timeWindows: selectedTimeWindows?.map((tw: any) => tw.id)
      }, {
        onSuccess: async (data) => {
          setSuggestedOptions(data || [])
          setIsSearchView(true);
        }
      })
    }
  };


  // const onRecentSearchClick = useCallback((row: RecentSearch) => {
  //   const { address, coordinates, service } = row;
  //   if (mapInstance) {
  //     const position = createLatLng(coordinates.latitude, coordinates.longitude);
  //     addMapMarker(position);
  //     mapInstance.setCenter(position);
  //     mapInstance.setZoom(17);
  //   }
  //   if (searchRef.current) {
  //     searchRef.current.value = address;
  //   }
  //   setSelectedService(service);
  //   setIsSearchView(true);
  // }, [addMapMarker, mapInstance]);

  const onSuggestedOptionClick = useCallback((row: SuggestedOption) => {
    const { coordinates } = row;
    if (mapInstance) {
      const destination = createLatLng(coordinates.latitude, coordinates.longitude);
      const origin = mapInstance.getCenter();
      if (origin) {
        calculateMapRoute(origin, destination);
      }
      setIsSearchView(true);
    }
  }, [calculateMapRoute, mapInstance]);


  const onBackClick = () => {
    setIsSearchView(false);
    setSuggestedOptions([]);
  };

  return (
    <Page title="FreshAir SmartMatcher">
      <Content type={'div'}>
        <Grid>
          <Grid.Cell size={{ xs: 12, md: 5 }}>
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              justifyContent: "start",
              paddingLeft: "10px",
              paddingRight: "10px",
              gap: "10px"
            }}>
              <input
                ref={searchRef}
                style={{
                  boxSizing: "border-box",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  width: "100%",
                  height: "50px",
                  padding: "10px",
                }}
                type="text"
                placeholder="Enter Address"
              />
              <Select
                placeholder="Select Service Area"
                value={selectedArea}
                onChange={handleServiceAreaChange}
              >
                {serviceAreasQuery.data?.map((area: {id: string, name: string}) => (
                  <Option key={area.id} value={area.id}>
                    {area.name}
                  </Option>
                ))}
              </Select>
              <div style={{ width: '100%', overflow: 'hidden' }}>
                <Combobox
                  label="Time Window Preference"
                  multiSelect
                  onSelect={(value) => {
                    setSelectedTimeWindows(value);
                  }}
                  selected={selectedTimeWindows}
                >
                  {timeWindowsList?.map((tw: any, index: number) => (
                    <Combobox.Option key={index} id={index} label={tw.name} />
                  ))}
                </Combobox>
              </div>
              {/* <Select
                placeholder="Select Service"
                value={selectedService || undefined}
                onChange={(value) => setSelectedService(value as string)}
                loading={isServicesLoading}
              >
                {services && services.map((service) => (
                  <Option key={service.id} value={service.id}>{service.name}</Option>
                ))}
              </Select> */}
              <div className="flex flex-row justify-start gap-4">
                <RadioGroup
                  onChange={(value: Preferences) => setPreferences(value)}
                  value={preferences}
                  ariaLabel="preferences"
                >
                  <RadioOption value="noPreferences" label="Optimize" />
                  <RadioOption value="asap" label="ASAP" />
                  <RadioOption value="dateRange" label="Date Range" />
                </RadioGroup>
              </div>
              {preferences === "dateRange" && (
                <div style={{ display: "flex", gap: "var(--space-base)" }}>
                  <DatePicker
                    onChange={(date: Date) => setDateRangeStart(date)}
                    selected={dateRangeStart}
                    minDate={new Date()}
                    activator={<Button variation="subtle" label={dateRangeStart ? `From: ${formatDate(dateRangeStart)}` : "Select from date"} />}
                  />
                  <DatePicker
                    onChange={(date: Date) => setDateRangeEnd(date)}
                    selected={dateRangeEnd}
                    minDate={dateRangeStart || new Date()}
                    activator={<Button variation="subtle" label={dateRangeEnd ? `To: ${formatDate(dateRangeEnd)}` : "Select to date"} />}
                  />
                </div>
              )}
              <div className="width-full" style={{ width: "100%" }}>
                <Button
                  variation="learning"
                  label="Find Best Options"
                  loading={isLoading}
                  disabled={isLoading}
                  onClick={findBestOptionsClick}
                />
              </div>

              {!isSearchView && (
                <div style={{
                  width: "100%",
                  display: "none",
                }}>
                  <Tabs>
                    <Tab label="Next Jobs">
                      <TabWhap>
                        <Card>
                          <Content>
                            <Heading level={4}>
                              <div style={{ display: "flex", gap: "3px" }}>
                                <Icon name="jobForms" color="text" />
                                Upcoming Jobs
                              </div>
                            </Heading>
                            {jobs && jobs.map((job) => (
                              <ListItem
                                key={job.id}
                                active={selectedJob === job.id}
                                title={`${formatDate(job.startDate, true)} - ${formatDate(job.endDate, true)}`}
                                subtitle={job.clientName || 'no client'}
                                caption={job.title || 'no title'}
                                onClick={() => setSelectedJob(job.id)}
                              />
                            ))}
                          </Content>
                        </Card>
                      </TabWhap>
                    </Tab>
                    <Tab label="Technicians">
                      <TabWhap>
                        <Card>
                          <Content>
                            <Heading level={4}>
                              <div style={{ display: "flex", gap: "3px" }}>
                                <Icon name="user" color="text" />
                                Technicians
                              </div>
                            </Heading>
                            {technicians && technicians.map((technician) => (
                              <ListItem
                                key={technician.id}
                                title={technician.fullName}
                                subtitle={technician.industry || ''}
                                caption={technician.email}
                                onClick={() => { }}
                              />
                            ))}
                          </Content>
                        </Card>
                      </TabWhap>
                    </Tab>
                    {/* <Tab label="Recent Searches">
                      <TabWhap>
                        <Card>
                          <Content>
                            <Heading level={4}>
                              <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                                <Icon name="search" color="text" />
                                Recent Searches
                              </div>
                            </Heading>
                            {RECENT_SEARCHES.map((search: RecentSearch, index: number) => (
                              <ListItem
                                key={index}
                                subtitle={`${search.address} - ${search.service}`}
                                onClick={() => onRecentSearchClick(search)}
                              />
                            ))}
                          </Content>
                        </Card>
                      </TabWhap>
                    </Tab> */}
                  </Tabs>
                </div>
              )}
              {isSearchView && (
                <TabWhap>
                  <Card>
                    <Content>
                      <Heading level={4}>
                        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                          <Button
                            label="Back"
                            onClick={onBackClick}
                            icon="arrowLeft"
                            size="small"
                            variation="subtle"
                          />
                          Suggested Options
                        </div>
                      </Heading>
                      {suggestedOptions.map((option, index: number) => (
                        <ListItem
                          key={index}
                          title={getTitle(option)}
                          subtitle={`${option.count} available slot${option.count > 1 ? 's' : ''}`}
                          caption={'This time slot is available based on your preferences.'}
                          onClick={() => {}}
                        />
                      ))}
                    </Content>
                  </Card>
                </TabWhap>
              )}
            </div>
          </Grid.Cell>
          <Grid.Cell size={{ xs: 12, md: 7 }}>
            <MapComponent center={{ lat: 41.8781, lng: -87.6298 }} zoom={12} />
          </Grid.Cell>
        </Grid>
      </Content>
    </Page>
  );
}
